import React from "react"
import { cn } from "~/lib/utils"
import { Card, CardContent } from "~/react/ui/card"
import { Icon } from "~/react/ui/icon"
import { Button } from "~/react/ui/button"
import { Confetti } from "./confetti"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "~/react/ui/dropdown-menu"
import { discovers_path, campaigns_path, new_leads_import_path, new_companies_import_path } from "~/lib/routes"

declare const i18n: {
  t: (key: string) => string
}

interface DashboardShortcutProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: string
  title: string
  description: string
  children: React.ReactNode
}

const DashboardShortcut = React.forwardRef<HTMLDivElement, DashboardShortcutProps>(
  ({ className, icon, title, description, children, ...props }, ref) => {
    return (
      <Card ref={ref} className={cn("dashboard-shortcut", className)} {...props}>
        <CardContent>
          <div className="dashboard-shortcut__icon">
            <Icon icon={icon} aria-hidden="true" />
          </div>
          <h3 className="dashboard-shortcut__title">{title}</h3>
          <p className="dashboard-shortcut__description">{description}</p>
          {children}
        </CardContent>
      </Card>
    )
  },
)

DashboardShortcut.displayName = "DashboardShortcut"

interface DashboardShortcutsProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  subtitle?: string
  variant?: "default" | "success"
  onClose?: () => void
}

const DashboardShortcuts = React.forwardRef<HTMLDivElement, DashboardShortcutsProps>(
  ({ className, title, subtitle, variant = "default", onClose, ...props }, ref) => {
    if (variant === "success") {
      return (
        <div ref={ref} className={cn("dashboard-shortcuts", "dashboard-shortcuts--success", className)} {...props}>
          <Confetti timeout={5} />
          <div className="dashboard-shortcuts__gradient" />
          {onClose && (
            <Button
              variant="ghost"
              iconOnly
              className="dashboard-shortcuts__close"
              onClick={onClose}
              aria-label="Close"
            >
              <Icon icon="times" aria-hidden="true" />
            </Button>
          )}
          <div className="dashboard-shortcuts__success-content">
            <div className="dashboard-shortcuts__success-icon">
              <Icon icon="circle-check" aria-hidden="true" variant="light" color="success" />
            </div>
            <h2 className="dashboard-shortcuts__success-title">
              {i18n.t("js.dashboard.index.shortcuts.success.title")}
            </h2>
            <span
              className="dashboard-shortcuts__success-description"
              dangerouslySetInnerHTML={{
                __html: i18n.t("js.dashboard.index.shortcuts.success.description_html"),
              }}
            />
          </div>
        </div>
      )
    }

    return (
      <div ref={ref} className={cn("dashboard-shortcuts", className)} {...props}>
        <div className="dashboard-shortcuts__logo">
          <img
            src="/images/hunter-logo-outline.svg"
            alt=""
            aria-hidden="true"
            className="dashboard-shortcuts__logo-img"
          />
        </div>
        <div className="dashboard-shortcuts__header">
          <h2 className="dashboard-shortcuts__title">{title}</h2>
          {subtitle && <p className="dashboard-shortcuts__subtitle">{subtitle}</p>}
        </div>
        <div className="dashboard-shortcuts__cards">
          <DashboardShortcut
            icon="file-import"
            title={i18n.t("js.dashboard.index.discover_card.title")}
            description={i18n.t("js.dashboard.index.discover_card.description")}
          >
            <Button variant="primary" size="sm" asChild>
              <a href={discovers_path()}>{i18n.t("js.dashboard.index.discover_card.cta")}</a>
            </Button>
          </DashboardShortcut>
          <DashboardShortcut
            icon="file-import"
            title={i18n.t("js.dashboard.index.import_card.title")}
            description={i18n.t("js.dashboard.index.import_card.description")}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="primary" size="sm">
                  {i18n.t("js.dashboard.index.import_card.cta")}
                  <Icon icon="chevron-down" size="xs" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem asChild closeOnClick>
                  <a href={new_companies_import_path()}>{i18n.t("js.dashboard.index.import_card.import_companies")}</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild closeOnClick>
                  <a href={new_leads_import_path()}>{i18n.t("js.dashboard.index.import_card.import_people")}</a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </DashboardShortcut>
          <DashboardShortcut
            icon="file-import"
            title={i18n.t("js.dashboard.index.campaigns_card.title")}
            description={i18n.t("js.dashboard.index.campaigns_card.description")}
          >
            <Button variant="primary" size="sm" asChild>
              <a href={campaigns_path()}>{i18n.t("js.dashboard.index.campaigns_card.cta")}</a>
            </Button>
          </DashboardShortcut>
        </div>
      </div>
    )
  },
)

DashboardShortcuts.displayName = "DashboardShortcuts"

export { DashboardShortcuts }
